import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BanknotesIcon, ChartBarIcon, ScaleIcon, GlobeAltIcon, FunnelIcon, ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { rapportService, recouvrementService, opportuniteService, alerteService, assistantService, objectifService } from '../../api/services';
import { AssistantSynthese, ObjectifAnnuel } from '../../types';
import { carbonApi } from '../../api/carbon';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Cockpit dirigeant (TF-008) : pilotage unifié CA + marge + cash + CO₂, complété par
 * le pipeline et les alertes. Compose les endpoints de reporting existants.
 */
export function CockpitDirigeant() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [synthese, setSynthese] = useState<AssistantSynthese | null>(null);
  const [loadingSynthese, setLoadingSynthese] = useState(false);
  const genererSynthese = async () => {
    setLoadingSynthese(true);
    try { setSynthese(await assistantService.syntheseDirigeant()); }
    finally { setLoadingSynthese(false); }
  };
  const [rapportMsg, setRapportMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [envoiRapport, setEnvoiRapport] = useState(false);
  const envoyerRapport = async () => {
    setEnvoiRapport(true);
    setRapportMsg(null);
    try {
      const r = await assistantService.envoyerRapportMensuel();
      setSynthese(r);
      setRapportMsg({ ok: true, text: 'Rapport de pilotage envoyé par e-mail à la société.' });
    } catch (e: any) {
      setRapportMsg({ ok: false, text: e?.response?.data?.message || "Échec de l'envoi (adresse e-mail société manquante ?)" });
    } finally {
      setEnvoiRapport(false);
    }
  };
  const [data, setData] = useState<{
    caHT: number; marge: number; tauxMarge: number | null;
    encours: number; soldeProjete: number; cashNet: number;
    pipelinePondere: number; nbOpportunites: number;
    co2: number; nbAlertes: number;
  } | null>(null);
  const [objectif, setObjectif] = useState<ObjectifAnnuel | null>(null);
  const [showObjEditor, setShowObjEditor] = useState(false);
  const [objCa, setObjCa] = useState('');
  const [objMarge, setObjMarge] = useState('');

  const saveObjectif = async () => {
    const annee = new Date().getFullYear();
    const saved = await objectifService.upsert({
      annee,
      caObjectif: objCa === '' ? null : Number(objCa),
      margeObjectif: objMarge === '' ? null : Number(objMarge),
    });
    setObjectif(saved);
    setShowObjEditor(false);
  };

  useEffect(() => {
    const today = new Date();
    const start = `${today.getFullYear()}-01-01`;
    const end = today.toISOString().slice(0, 10);
    (async () => {
      try {
        const [marge, balance, treso, pipeline, alertes, carbon] = await Promise.all([
          rapportService.getMarge(start, end, 'produit'),
          recouvrementService.getBalanceAgee(),
          recouvrementService.getTresorerie(3, 0),
          opportuniteService.getPipeline(),
          alerteService.getAll(),
          carbonApi.summary().catch(() => ({ totalKgCo2e: 0 } as any)),
        ]);
        objectifService.get().then(setObjectif).catch(() => {});
        const dernier = treso.periodes[treso.periodes.length - 1];
        const cashNet = treso.periodes.reduce((s, p) => s + p.net, 0);
        setData({
          caHT: marge.totalRevenuHT,
          marge: marge.totalMarge,
          tauxMarge: marge.tauxMargePct,
          encours: balance.totaux.total,
          soldeProjete: dernier ? dernier.soldeProjete : 0,
          cashNet,
          pipelinePondere: pipeline.previsionPonderee,
          nbOpportunites: pipeline.nbOuvertes,
          co2: carbon.totalKgCo2e ?? 0,
          nbAlertes: alertes.length,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="page-title">Cockpit dirigeant</h1>
          <p className="page-subtitle">Chiffre d'affaires · marge · trésorerie · empreinte carbone</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => {
            setObjCa(objectif?.caObjectif != null ? String(objectif.caObjectif) : '');
            setObjMarge(objectif?.margeObjectif != null ? String(objectif.margeObjectif) : '');
            setShowObjEditor((v) => !v);
          }} className="btn-secondary">🎯 Objectifs</button>
          <button onClick={genererSynthese} className="btn-secondary" disabled={loadingSynthese}>
            {loadingSynthese ? 'Analyse…' : '✨ Synthèse IA'}
          </button>
          {user?.role === 'ADMIN' && (
            <button onClick={envoyerRapport} className="btn-secondary" disabled={envoiRapport}
                    title="Envoie immédiatement le rapport de pilotage par e-mail (envoi aussi automatique chaque 1er du mois)">
              {envoiRapport ? 'Envoi…' : '✉️ Rapport mensuel'}
            </button>
          )}
        </div>
      </div>

      {rapportMsg && (
        <div className={`text-sm rounded-md p-3 ${rapportMsg.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}>
          {rapportMsg.text}
        </div>
      )}

      {showObjEditor && (
        <div className="card flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Objectif CA {new Date().getFullYear()} (TND)</label>
            <input className="input-field w-40" type="number" value={objCa} onChange={(e) => setObjCa(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Objectif marge (TND)</label>
            <input className="input-field w-40" type="number" value={objMarge} onChange={(e) => setObjMarge(e.target.value)} />
          </div>
          <button onClick={saveObjectif} className="btn-primary">Enregistrer</button>
        </div>
      )}

      {synthese && (
        <div className="card border-l-4 border-l-accent-500 bg-accent-50/40">
          <p className="text-sm whitespace-pre-line text-gray-800">{synthese.synthese}</p>
          {!synthese.genereParIa && (
            <p className="text-xs text-gray-400 mt-2">Mode hors-ligne (LLM non configuré) — synthèse factuelle.</p>
          )}
        </div>
      )}

      {data.nbAlertes > 0 && (
        <button onClick={() => navigate('/alertes')}
                className="w-full flex items-center gap-2 bg-amber-50 border-l-4 border-l-amber-500 rounded-md p-3 text-sm text-amber-800">
          <ExclamationTriangleIcon className="h-5 w-5" />
          {data.nbAlertes} alerte(s) à traiter — ouvrir le centre d'alertes
        </button>
      )}

      {/* Les 4 piliers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Pilier title="Chiffre d'affaires (HT, année)" icon={ChartBarIcon} accent="text-accent-700">
          <MoneyDisplay amount={data.caHT} className="text-2xl font-bold" />
        </Pilier>
        <Pilier title="Marge" icon={ScaleIcon} accent="text-emerald-700">
          <MoneyDisplay amount={data.marge} className="text-2xl font-bold text-emerald-700" />
          <p className="text-xs text-gray-500 mt-1">
            Taux : {data.tauxMarge == null ? '—' : `${data.tauxMarge.toFixed(1)} %`}
          </p>
        </Pilier>
        <Pilier title="Trésorerie (net 90 j)" icon={BanknotesIcon} accent="text-blue-700">
          <MoneyDisplay amount={data.cashNet} className={`text-2xl font-bold ${data.cashNet < 0 ? 'text-red-600' : ''}`} />
          <p className="text-xs text-gray-500 mt-1">
            Solde projeté : <MoneyDisplay amount={data.soldeProjete} size="sm" />
          </p>
        </Pilier>
        <Pilier title="Empreinte carbone (mois)" icon={GlobeAltIcon} accent="text-green-700">
          <p className="text-2xl font-bold text-green-700">{data.co2.toFixed(1)} <span className="text-sm font-normal">kg CO₂e</span></p>
        </Pilier>
      </div>

      {/* Objectifs annuels (TF-075) */}
      {objectif && (objectif.caObjectif || objectif.margeObjectif) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ObjectifBar label={`Objectif CA ${objectif.annee}`} realise={data.caHT} objectif={objectif.caObjectif} />
          <ObjectifBar label={`Objectif marge ${objectif.annee}`} realise={data.marge} objectif={objectif.margeObjectif} />
        </div>
      )}

      {/* Secondaires */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button onClick={() => navigate('/crm/pipeline')} className="card text-left hover:shadow-sm transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 flex items-center gap-1"><FunnelIcon className="h-4 w-4" /> Pipeline pondéré</p>
              <MoneyDisplay amount={data.pipelinePondere} className="text-xl font-semibold" />
            </div>
            <span className="text-xs text-gray-400">{data.nbOpportunites} ouvertes</span>
          </div>
        </button>
        <button onClick={() => navigate('/recouvrement/impayes')} className="card text-left hover:shadow-sm transition">
          <p className="text-sm text-gray-500 flex items-center gap-1"><BanknotesIcon className="h-4 w-4" /> Encours clients</p>
          <MoneyDisplay amount={data.encours} className="text-xl font-semibold text-amber-700" />
        </button>
      </div>
    </div>
  );
}

interface PilierProps {
  title: string;
  icon: React.FC<{ className?: string }>;
  accent?: string;
  children: React.ReactNode;
}
const ObjectifBar: React.FC<{ label: string; realise: number; objectif: number | null }> =
  ({ label, realise, objectif }) => {
    if (!objectif || objectif <= 0) return null;
    const pct = Math.max(0, Math.round((realise / objectif) * 100));
    const color = pct >= 100 ? 'bg-emerald-500' : pct >= 70 ? 'bg-amber-500' : 'bg-blue-500';
    return (
      <div className="card">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">{label}</span>
          <span className="font-semibold">{pct}%</span>
        </div>
        <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full ${color}`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
        <div className="mt-1 flex justify-between text-xs text-gray-400">
          <MoneyDisplay amount={realise} size="sm" />
          <span>/ <MoneyDisplay amount={objectif} size="sm" /></span>
        </div>
      </div>
    );
  };

const Pilier: React.FC<PilierProps> = ({ title, icon: Icon, accent = 'text-gray-700', children }) => (
  <div className="card">
    <div className="flex items-start justify-between">
      <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">{title}</p>
      <Icon className={`h-5 w-5 ${accent}`} />
    </div>
    <div className="mt-2">{children}</div>
  </div>
);

export default CockpitDirigeant;
